import { ModelContract, ModuleDescription } from "../../shared/module-description";
import { IUserAuthContract } from "../../student/auth/model";
import { createEffect, createEvent, createStore, sample, combine } from "effector";
import { reset } from "patronum";
import {
    ICurrentCourse,
    IStudentCourse,
    IStudentLesson,
    IStudentTask,
    IStudentTopic
} from "../../shared/entities/enrollments";
import { toastShow } from "@features/notification/model";
import { ICustomError } from "@features/shared/entities/notification";
import { StudentProgressApi } from "@features/shared/api/course-data.api";
import { ILessonsContract } from "@features/lessons/model";
import { CourseStarted, LessonStarted } from "@features/shared/events";
import { $currentCourse } from "@features/courses/ui/current";
import { trackTopicStartedFx } from "@features/topics/ui/current";
import { $topics } from "@features/topics/model";
import { routePathNameChanged } from "@features/app/model";
import { delay } from "patronum";
import { ICourse } from "@features/shared/entities/courses";
import { $courses, $draftCourses } from "@features/courses/model";
import { UserEnrollmentStatus } from "@features/shared/entities/status";

// TODO: rename events

export const enrollCoursesLoaded = createEvent();
export const enrollTopicsLoaded = createEvent();
const enrollTopicsLoadingFinished = createEvent();
export const enrollLessonsLoaded = createEvent();
export const enrollTasksLoaded = createEvent();
export const enrollCourse = createEvent<number>();
export const enrollTopic = createEvent<{ courseId: number; topicId: number }>();
export const enrollLesson = createEvent<{ topicId: number; lessonId: number; skipAnalytics?: boolean }>();
export const enrollTask = createEvent<{ lessonId: number; taskId: string }>();
const lessonEnrollmentReceived = createEvent<IStudentLesson>();
const clearLocalStorageFlag = createEvent();

// Internal event for draft course enrollments (analytics only, doesn't trigger API)
const draftCourseEnrollmentStarted = createEvent<{ topicId: number; lessonId: number }>();

// Delay route change events to allow normal enrollment flow to complete
// For draft courses: when user clicks lesson -> route changes -> we wait 1000ms to ensure
// all stores are updated before checking auto-enrollment conditions for draft courses only
const delayedRouteChange = delay(routePathNameChanged, 1000);

export const getEnrollmentsCoursesFx = createEffect<void, IStudentCourse[]>();
export const getEnrollmentsTopicsFx = createEffect<void, IStudentTopic[]>();
export const getEnrollmentsLessonsFx = createEffect<void, IStudentLesson[]>();
export const getEnrollmentsTasksFx = createEffect<void, IStudentTask[]>();
export const enrollCourseFx = createEffect<number, unknown>();
export const enrollTopicFx = createEffect<{ courseId: number; topicId: number }, unknown>();
export const enrollLessonFx = createEffect<{ topicId: number; lessonId: number }, unknown>();
export const enrollTaskFx = createEffect<{ lessonId: number; taskId: string }, unknown>();

// Auto-enrollment effect specifically for draft courses
// Only enrolls the lesson, not the course or topic to keep clean statistics
export const autoEnrollOnLessonOpenFx = createEffect<
    {
        params: { courseId: number; topicId: number; lessonId: number };
        enrollmentsLessons: IStudentLesson[];
        courses: ICourse[];
        draftCourses: ICourse[];
    },
    void
>(async ({ params, enrollmentsLessons, courses, draftCourses }) => {
    const { topicId, lessonId, courseId } = params;

    // Find if this is a draft course
    let course = courses.find((c) => c.id === courseId);
    if (!course) {
        course = draftCourses.find((c) => c.id === courseId);
    }

    // Only proceed if this is a draft course
    if (!course?.draft) {
        return;
    }

    // For draft courses, only enroll the lesson
    const lessonEnrollment = enrollmentsLessons.find((e) => e.lesson_id === lessonId);
    if (!lessonEnrollment) {
        // Trigger analytics event before API call (won't trigger enrollLessonFx to avoid double API calls)
        draftCourseEnrollmentStarted({ topicId, lessonId });

        try {
            await StudentProgressApi.enrollLesson({ topicId, lessonId });
        } catch (error) {
            if (!(error instanceof Error) || !error.message?.includes("already enrolled")) {
                throw error;
            }
        }
    }
});

export const $enrollmentsCourses = createStore<IStudentCourse[]>([], { skipVoid: false }).on(
    getEnrollmentsCoursesFx.doneData,
    (_, p) => p.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
);
export const $enrollmentsTopics = createStore<IStudentTopic[]>([], { skipVoid: false }).on(
    getEnrollmentsTopicsFx.doneData,
    (_, p) => p.sort((a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime())
);

export const $enrollmentsLessons = createStore<IStudentLesson[]>([], { skipVoid: false })
    .on(getEnrollmentsLessonsFx.doneData, (_, p) =>
        p.sort((a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime())
    )
    .on(lessonEnrollmentReceived, (lessons, received) => {
        const existing = lessons.find((lesson) => lesson.lesson_id === received.lesson_id);
        if (!existing) return [received, ...lessons];
        if (existing.status === received.status) return lessons;
        return lessons.map((lesson) =>
            lesson.lesson_id === received.lesson_id
                ? { ...lesson, status: received.status, completed_at: received.completed_at }
                : lesson
        );
    });
export const $enrollmentsTasks = createStore<IStudentTask[]>([], { skipVoid: false }).on(
    getEnrollmentsTasksFx.doneData,
    (_, p) => p
);

// Loading states
export const $enrollmentsCoursesLoading = createStore(false).on(
    getEnrollmentsCoursesFx.pending,
    (_, pending) => pending
);
export const $enrollmentsLessonsLoading = createStore(false).on(
    getEnrollmentsLessonsFx.pending,
    (_, pending) => pending
);

getEnrollmentsCoursesFx.use(async () => {
    const response = await StudentProgressApi.getCoursesEnrollments();
    return response;
});

getEnrollmentsTopicsFx.use(async () => {
    const response = await StudentProgressApi.getTopicsEnrollments();
    return response;
});

getEnrollmentsLessonsFx.use(async () => {
    const response = await StudentProgressApi.getLessonsEnrollments();
    return response;
});

getEnrollmentsTasksFx.use(async () => {
    const response = await StudentProgressApi.getTasksEnrollemnents();
    return response;
});

enrollCourseFx.use(async (courseId: number) => {
    const response = await StudentProgressApi.enrollCourse(courseId);
    return response;
});

enrollTopicFx.use(async ({ courseId, topicId }) => {
    const response = await StudentProgressApi.enrollTopic({ courseId, topicId });
    return response;
});

enrollLessonFx.use(async ({ topicId, lessonId }) => {
    const response = await StudentProgressApi.enrollLesson({ topicId, lessonId });
    return response;
});

const trackLessonStartedOnEnrollmentFx = createEffect<
    {
        enrollmentData: { topicId: number; lessonId: number };
        currentCourse: ICurrentCourse | null;
        courseLessons: any[];
        lessons: any[];
    },
    void
>(({ enrollmentData, currentCourse, courseLessons, lessons }) => {
    if (!currentCourse) return;

    // Find lesson in courseLessons (structured by topics) or lessons (flat array for challenges)
    const findLesson = (lessonId: number) => {
        // Try courseLessons first (for regular courses)
        for (const topicWithLessons of courseLessons) {
            const lesson = topicWithLessons.lessons?.find((l: any) => l.id === lessonId);
            if (lesson) return lesson;
        }
        // Fallback to flat lessons array (for challenges)
        return lessons.find((l: any) => l.id === lessonId);
    };

    const lesson = findLesson(enrollmentData.lessonId);

    // Send analytics only if we found the lesson
    if (lesson) {
        LessonStarted({
            courseType: currentCourse.type,
            courseId: currentCourse.id,
            topicId: enrollmentData.topicId,
            lessonId: enrollmentData.lessonId,
            lessonTitle: lesson.title,
            dayNumber: lesson.number
        });
    }
});

enrollTaskFx.use(async ({ lessonId, taskId }) => {
    const response = await StudentProgressApi.enrollTask({ lessonId, taskId });

    return response;
});

const clearLocalStorageFlagFx = createEffect<void, void>(() => {
    if (typeof window !== "undefined") {
        localStorage.removeItem("productOnboardingFinished");
    }
});

sample({
    clock: enrollCoursesLoaded,
    target: getEnrollmentsCoursesFx
});

sample({
    clock: enrollTopicsLoaded,
    target: getEnrollmentsTopicsFx
});

sample({ clock: getEnrollmentsTopicsFx.done, target: enrollTopicsLoadingFinished });

sample({
    clock: enrollLessonsLoaded,
    target: getEnrollmentsLessonsFx
});

sample({
    clock: enrollTasksLoaded,
    target: getEnrollmentsTasksFx
});

sample({
    clock: enrollCourse,
    target: enrollCourseFx
});

sample({
    clock: [enrollCourseFx.doneData],
    target: enrollCoursesLoaded
});

const trackCourseStarted = createEffect(({ id, title, type }: Partial<ICurrentCourse>) =>
    CourseStarted(id!, title!, type!)
);

sample({
    clock: [enrollCourseFx.doneData],
    source: $currentCourse,
    filter: ($currentCourse) => $currentCourse != null,
    fn: ($currentCourse: ICurrentCourse) => ({
        id: $currentCourse.id,
        title: $currentCourse.title,
        type: $currentCourse.type
    }),
    target: trackCourseStarted
});

sample({
    clock: enrollTopic,
    target: enrollTopicFx
});

sample({
    clock: [enrollTopicFx.doneData],
    target: enrollTopicsLoaded
});

sample({
    clock: enrollLesson,
    fn: ({ topicId, lessonId }) => ({ topicId, lessonId }),
    target: enrollLessonFx
});

sample({
    clock: enrollLessonFx.doneData,
    target: enrollLessonsLoaded
});

// This sample is moved to EnrollmentsModule.run() to avoid circular dependency

sample({
    clock: enrollTopicFx.done,
    source: { topics: $topics, courses: $courses, draftCourses: $draftCourses },
    filter: ({ courses, draftCourses }, { params }) =>
        [...courses, ...draftCourses].find((course) => course.id === params.courseId)?.type !== "challenge",
    fn: ({ topics }, { params }) => ({
        courseId: params.courseId,
        topicId: params.topicId,
        title: topics.find((topic) => topic.id === params.topicId)?.title ?? ""
    }),
    target: trackTopicStartedFx
});

sample({
    clock: enrollTask,
    target: enrollTaskFx
});

sample({
    clock: [enrollTaskFx.doneData],
    target: enrollTasksLoaded
});

// Refresh lesson enrollments after draft course auto-enrollment
sample({
    clock: autoEnrollOnLessonOpenFx.doneData,
    target: enrollLessonsLoaded
});

sample({
    clock: delayedRouteChange,
    source: {
        enrollmentsLessons: $enrollmentsLessons,
        courses: $courses,
        draftCourses: $draftCourses
    },
    filter: ({ enrollmentsLessons, courses, draftCourses }, url: string) => {
        // Check that this is a lesson page
        const lessonMatch = url.match(/\/course\/(\d+)\/topic\/(\d+)\/lesson\/(\d+)/);
        if (!lessonMatch) return false;

        const [, courseId, , lessonId] = lessonMatch;
        const courseIdNum = parseInt(courseId, 10);
        const lessonIdNum = parseInt(lessonId, 10);

        // Find if this is a draft course
        let course = courses.find((c) => c.id === courseIdNum);
        if (!course) {
            course = draftCourses.find((c) => c.id === courseIdNum);
        }

        // Only proceed for draft courses
        if (!course?.draft) return false;

        // Check that there is no lesson enrollment yet
        const hasLessonEnrollment = enrollmentsLessons.some((e) => e.lesson_id === lessonIdNum);
        return !hasLessonEnrollment;
    },
    fn: ({ enrollmentsLessons, courses, draftCourses }, url: string) => {
        const lessonMatch = url.match(/\/course\/(\d+)\/topic\/(\d+)\/lesson\/(\d+)/);
        const [, courseId, topicId, lessonId] = lessonMatch!;

        return {
            params: {
                courseId: parseInt(courseId, 10),
                topicId: parseInt(topicId, 10),
                lessonId: parseInt(lessonId, 10)
            },
            enrollmentsLessons,
            courses,
            draftCourses
        };
    },
    target: autoEnrollOnLessonOpenFx
});

sample({
    clock: [
        getEnrollmentsCoursesFx.fail,
        getEnrollmentsTopicsFx.fail,
        getEnrollmentsLessonsFx.fail,
        getEnrollmentsTasksFx.fail,
        enrollCourseFx.fail,
        enrollTopicFx.fail,
        enrollLessonFx.fail,
        enrollTaskFx.fail
    ],
    filter: ({ error }: { error: ICustomError }) => error?.error?.title !== null,
    fn: ({ error }: { error: ICustomError }) => error?.error,
    target: toastShow
});

export const $enrollmentsCoursesLoaded = createStore<IStudentCourse[]>([]).on(
    getEnrollmentsCoursesFx.doneData,
    (_, payload) => payload
);

sample({
    clock: clearLocalStorageFlag,
    target: clearLocalStorageFlagFx
});

const contract = new ModelContract(
    {
        $enrollmentsCourses,
        $enrollmentsTopics,
        $enrollmentsLessons,
        $enrollmentsTasks,
        $enrollmentsCoursesLoading,
        $enrollmentsLessonsLoading
    },
    {
        enrollCoursesLoaded,
        enrollTopicsLoaded,
        enrollLessonsLoaded,
        enrollTasksLoaded,
        enrollTopicsLoadingFinished,
        enrollCourse,
        enrollTopic,
        enrollLesson,
        enrollTask
    },
    {
        getEnrollmentsCoursesFx,
        getEnrollmentsTopicsFx,
        getEnrollmentsLessonsFx,
        getEnrollmentsTasksFx,
        autoEnrollOnLessonOpenFx
    },
    {}
);

export type IEnrollmentsContract = typeof contract;

export class EnrollmentsModule extends ModuleDescription<IEnrollmentsContract> {
    public lessonsModules: ILessonsContract | undefined;

    constructor(public userAuthModule: IUserAuthContract) {
        super();
    }

    setDependency(mod: ILessonsContract) {
        this.lessonsModules = mod;
    }

    getDefaultContract() {
        return contract;
    }

    run(): void {
        sample({
            source: this.userAuthModule.stores.$student,
            clock: [this.userAuthModule.events.authenticated, this.lessonsModules?.events.lessonCompletionTracked],
            filter: (student) => student !== null,
            target: [enrollCoursesLoaded, enrollTopicsLoaded, enrollLessonsLoaded, enrollTasksLoaded]
        });

        sample({
            clock: this.lessonsModules!.effects.lessonCompletedFx.doneData,
            fn: ({ id, student_id, topic_id, lesson_id, started_at, completed_at }): IStudentLesson => ({
                id,
                student_id,
                topic_id,
                lesson_id,
                status: UserEnrollmentStatus.Completed,
                started_at,
                completed_at
            }),
            target: lessonEnrollmentReceived
        });

        sample({
            source: this.userAuthModule.events.logoutStudent,
            target: clearLocalStorageFlag
        });

        reset({
            clock: this.userAuthModule.events.logoutStudent,
            target: [$enrollmentsCourses, $enrollmentsTopics, $enrollmentsLessons, $enrollmentsTasks]
        });

        // Track lesson_started analytics when enrollment is requested (before API call)
        // This sends analytics immediately, even if API fails (better for tracking issues)
        // Duplicates are prevented by:
        // - UI filters check if enrollment exists before calling enrollLesson
        // - autoEnrollOnLessonOpenFx checks for existing enrollment before calling draftCourseEnrollmentStarted

        // Analytics for regular course enrollments
        // skipAnalytics prevents duplicate tracking when manualLessonStartedFx handles it
        sample({
            clock: enrollLesson,
            source: {
                currentCourse: $currentCourse,
                courseLessons: this.lessonsModules!.stores.$courseLessons,
                lessons: this.lessonsModules!.stores.$lessons
            },
            filter: (_, enrollmentData) => !enrollmentData.skipAnalytics,
            fn: (source, enrollmentData) => ({
                enrollmentData: { topicId: enrollmentData.topicId, lessonId: enrollmentData.lessonId },
                currentCourse: source.currentCourse,
                courseLessons: source.courseLessons,
                lessons: source.lessons
            }),
            target: trackLessonStartedOnEnrollmentFx
        });

        // Analytics for draft course enrollments
        sample({
            clock: draftCourseEnrollmentStarted,
            source: {
                currentCourse: $currentCourse,
                courseLessons: this.lessonsModules!.stores.$courseLessons,
                lessons: this.lessonsModules!.stores.$lessons
            },
            fn: (source, enrollmentData) => ({
                enrollmentData,
                currentCourse: source.currentCourse,
                courseLessons: source.courseLessons,
                lessons: source.lessons
            }),
            target: trackLessonStartedOnEnrollmentFx
        });
    }
}
